/**
 * Default-renderer copy-on-write (`SheetRendererCow`): a source row's
 * synthesized default renderer is virtual until a display option is written
 * to it. The first write materializes it into a real renderer row (snapshot →
 * fresh id → re-add), marks the source render-less and persists both rows;
 * a refusal of either takes the materialize back. A reset deletes the real
 * renderer and re-synthesizes the virtual one. Reads dual-read the renderer's
 * own bag before the source's.
 *
 * It owns no state: the rows, the tree, the render-less flags and the
 * row-field ledger stay the sheet's, handed in through `SheetRendererCow.Host`
 * (`Sheet` builds one over its private members): the backend and sheet id,
 * `addRow` and the default-renderer synthesis, not the sheet itself. It
 * names no sheet type, so it sits below `Sheet` and outside every cycle.
 */

import { Tracing } from "@tento-core/tracing";
import { Uuid } from "@tento-core/uuid";
import type { Option } from "../core/option.ts";
import { RowWriteError } from "../core/errors.ts";
import { RowId } from "../ids/row-id.ts";
import { isSyntheticDefaultRendererId, type RowLocalId } from "../ids/row-local-id.ts";
import { RowWrites } from "../ledger/row-writes.ts";
import type { ModelRow } from "../rows/model-row.ts";
import { shapeFromMeta } from "../rows/variants/hydrate-default-renderer.ts";
import { SourceRow } from "../rows/variants/source-row.ts";
import { RowMetadataStore } from "../stores/index.ts";
import type { RawRow } from "../wire/raw-sheet-wire.ts";
import type { Backend } from "../backends/rows-backend.ts";
import type { SheetId } from "../ids/sheet-id.ts";
import type { AddRowSpec, SheetStores } from "./rows-sheet.ts";
import { remapRowIds } from "./sheet-row-codec.ts";

/** A refused attribute write's context: which attributes of which row were not saved. */
export function attrContext(rowId: RowLocalId, keys: readonly string[]): string {
  return `the attributes ${keys.join(", ")} of ${rowId.rawId} were not saved`;
}

/**
 * The `RowWriteError("update")` of a refused flag write the ledger already
 * wrapped (`err`, run with no context): `context` leads its details, and the
 * backend's own error stays its cause.
 */
export function flagRefused(err: unknown, context: string): RowWriteError {
  const error = RowWriteError.after(err, "update", "PATCH", "/api/rows/{rowKey}", context);
  if (err instanceof RowWriteError && err.cause !== undefined) error.cause = err.cause;
  return error;
}

/** Types that go with `SheetRendererCow`: `AttrWrite`, `Patch`, `Host`. */
export namespace SheetRendererCow {
  /**
   * The verdict of a display-attribute write (`setRendererDisplayAttr`, the
   * row-options seam): `"backend"` — the backend accepted it;
   * `"deferred-to-promote"` — the sheet is ephemeral, nothing was sent, and
   * the value persists with the sheet when it is promoted. A refused or
   * unanswered write does not resolve: it rejects with the backend's error.
   */
  export type AttrWrite = {
    readonly persisted: "backend" | "deferred-to-promote";
  };

  /** A display-attribute patch: scalar values by key (`null` clears). */
  export type Patch = Readonly<Record<string, string | number | boolean | null>>;

  /** What the copy-on-write reads and writes of its sheet. */
  export interface Host {
    /** The sheet's backend: a materialized renderer is created there, a refused one deleted. */
    readonly backend: Pick<Backend, "addRow" | "deleteRow">;
    /** The sheet the renderer rows are created in. */
    sheetId(): Option<SheetId>;
    /** Add a row to the sheet (`Sheet.addRow`). */
    addRow(spec: AddRowSpec): ModelRow;
    /** The default renderer the sheet would synthesize under `source` for `shape` (`hydrateDefaultRenderer`), or `null`. */
    synthesizeRenderer(source: SourceRow, shape: Option<string>): Option<ModelRow>;
    /** The sheet's tree, render-less flags and row metadata. */
    stores(): Pick<SheetStores, "tree" | "renderless" | "rowMetadata">;
    /** The sheet's row-field ledger. */
    rowWrites(): RowWrites;
    /** Whether the sheet is ephemeral (nothing is persisted until it is promoted). */
    isEphemeral(): boolean;
    /** The row `id` names, or unset. */
    row(id: RowLocalId): Option<ModelRow>;
    has(id: RowLocalId): boolean;
    /** The row `id` names, or the sheet's unaddressable-row error for `step`. */
    requireRow(id: RowLocalId, step: string): ModelRow;
    /** Drop `id` and its subtree from the sheet. */
    dropRowSubtree(id: RowLocalId): void;
    /** Take in a synthesized default renderer `child` as the first child of `parentId`. */
    adoptChild(child: ModelRow, parentId: RowLocalId): void;
    /** `row` as its wire row (`snapshotRow` over the sheet's stores). */
    snapshot(row: ModelRow): RawRow;
    /** Paint `change` on `rowId`'s fields, then persist it, answering the verdict. */
    writeRowFields(rowId: RowLocalId, change: RowWrites.Change, context: string): Promise<AttrWrite>;
    /** Enter `rowId`'s store-held wire keys (the render-less flag) as an attribute write. */
    beginWireAttributes(rowId: RowLocalId): RowWrites.Write;
    /** How a row-field write of `rowId` reaches the backend. */
    rowTransport(rowId: RowLocalId): RowWrites.Transport;
    /** How the sheet enters a row write. */
    writeOpts(): RowWrites.Begin;
  }
}

/** A sheet's default-renderer copy-on-write (see the module note). */
export class SheetRendererCow {
  readonly #host: SheetRendererCow.Host;

  constructor(host: SheetRendererCow.Host) {
    this.#host = host;
  }

  /**
   * Copy-on-write: materialize a source's auto-synthesized default renderer
   * into a REAL, persisted renderer row, and flip the source render-less so
   * `hydrate` / `addRow` stop synthesizing a virtual one beneath it. The
   * renderer becomes a first-class row whose display options can live on it
   * (renderer-attr) rather than on the source's attribute bag. Persists both
   * the new renderer row and the source's render-less flag.
   *
   * Returns the materialized renderer's real `RowLocalId`, or `null` when the
   * source has no synthesizable renderer (obj dtypes, unsupported slots). If the
   * source is already render-less, returns its existing real renderer child.
   *
   * Mirrors `detachAlias`'s virtual→real promote (snapshot → fresh id →
   * `remapRowIds` → drop → re-add → persist). Kept inline rather than
   * sharing a `promoteVirtualToReal` helper, to avoid disturbing
   * `detachAlias`'s re-expansion-sensitive detach-before-re-add ordering; a
   * later cleanup can factor the common core out.
   */
  async ensureRendererMaterialized(
    sourceId: RowLocalId,
  ): Promise<Option<RowLocalId>> {
    const { rendererId, created } = this.#materializeRendererLocal(sourceId);
    let effectiveId = rendererId;
    // The local tree already holds the real renderer + the source's
    // render-less flag. An ephemeral (not-yet-promoted) sheet has no server
    // record to write to: the renderer round-trips through serialize →
    // hydrate on promote instead. Any other sheet persists both rows; a
    // refusal of either takes the materialize back (the renderer created on
    // the server deleted again) and rejects with `RowWriteError`.
    if (
      created && rendererId !== null && rendererId !== undefined &&
      !this.#host.isEphemeral()
    ) {
      const source = this.#host.row(sourceId);
      const renderer = this.#host.row(rendererId);
      if (source != null && renderer != null) {
        let minted: Option<RowId>;
        try {
          minted = await this.#host.backend.addRow(
            this.#host.snapshot(renderer),
            this.#host.sheetId(),
            null,
            null,
            null,
          );
        } catch (err) {
          this.#undoRendererMaterialize(sourceId, rendererId);
          throw RowWriteError.after(
            err,
            "add",
            "POST",
            "/api/rows",
            `the renderer row of ${sourceId.rawId} was not created (nothing reached the server)`,
          );
        }
        if (minted != null && minted.toString() !== rendererId.rawId) {
          effectiveId = this.#rekeyRowLocal(rendererId, minted) ?? rendererId;
        }
        const flag = this.#host.beginWireAttributes(sourceId);
        try {
          await this.#host.rowWrites().run(flag, this.#host.rowTransport(sourceId), "");
        } catch (err) {
          const createdRow = minted ?? RowId.fromLocalId(rendererId);
          const deleted = await this.#host.backend.deleteRow(createdRow, null).then(() => true, () => false);
          this.#undoRendererMaterialize(sourceId, effectiveId ?? rendererId);
          throw flagRefused(
            err,
            `the render-less flag of ${sourceId.rawId} was not saved; the renderer row ` +
              `${createdRow.toString()} had reached the server and ` +
              (deleted ? "was deleted again" : "could not be deleted (it remains on the server)"),
          );
        }
      }
    }
    return effectiveId;
  }

  /**
   * Local-only half of `ensureRendererMaterialized`: snapshot the source's
   * synthetic default renderer, promote it to a real in-memory row, and mark
   * the source render-less — WITHOUT touching the backend. Returns the real
   * renderer id and whether it was created this call (so callers persist
   * exactly once, re-fetching the rows they persist by id). Splitting the
   * local mutation out lets the write path patch + paint first, then persist
   * best-effort (optimistic-local-first).
   *
   * No-op (returns the existing real renderer, `created: false`) when the
   * source is already render-less; returns `rendererId: null` when the source
   * has no synthesizable renderer (e.g. obj dtypes) so the write falls back to
   * the source's own bag, preserving today's behavior for those rows.
   */
  #materializeRendererLocal(sourceId: RowLocalId): {
    rendererId: Option<RowLocalId>;
    created: boolean;
  } {
    const source = this.#host.row(sourceId);
    if (!(source instanceof SourceRow)) {
      return { rendererId: null, created: false };
    }

    const realRendererChild = (): Option<RowLocalId> => {
      for (const childId of this.#host.stores().tree.children(sourceId)) {
        const child = this.#host.row(childId);
        if (child != null && child.descriptor.role === "renderer") {
          return childId;
        }
      }
      return null;
    };

    // Already materialized — return the existing real renderer child.
    if (this.#host.stores().renderless.isRenderless(sourceId)) {
      return { rendererId: realRendererChild(), created: false };
    }

    // Locate the auto-synthesized virtual renderer to promote.
    let synthetic: Option<ModelRow> = null;
    for (const childId of this.#host.stores().tree.children(sourceId)) {
      if (isSyntheticDefaultRendererId(childId)) {
        synthetic = this.#host.row(childId) ?? null;
        break;
      }
    }
    if (synthetic == null) {
      return { rendererId: null, created: false };
    }

    // Snapshot → fresh real id → remap → drop → mark render-less → re-add real.
    // `addRow` rejects the `cells` field (renderers carry none anyway), so strip
    // it. `mark` before re-add so the source won't re-synthesize on any
    // subsequent add; the renderer re-add itself never re-synthesizes (it's not
    // a SourceRow).
    const idRemap = new Map<string, string>([
      [synthetic.id.rawId, Uuid.make()],
    ]);
    const promoted = remapRowIds(
      this.#host.snapshot(synthetic),
      idRemap,
    );
    this.#host.dropRowSubtree(synthetic.id);
    this.#host.stores().renderless.mark(sourceId);
    const { cells: _cells, ...spec } = promoted;
    const real = this.#host.addRow(spec);

    Tracing.log("renderer-cow/materialize", {
      source: sourceId.rawId,
      renderer: real.id.rawId,
    });

    return { rendererId: real.id, created: true };
  }

  /**
   * Shared core: materialize the source's default renderer (if needed), patch
   * `patch` onto the renderer's own bag (or the source's, when the source has
   * no synthesizable renderer), paint, then persist and return the verdict
   * (see `setRendererDisplayAttr`). On first materialize the renderer's
   * snapshot already carries `patch`, so a single `addRow` persists both —
   * avoiding an `addRow` → `updateRow` ordering race.
   */
  async materializeAndPatchRenderer(
    sourceId: RowLocalId,
    patch: SheetRendererCow.Patch,
  ): Promise<SheetRendererCow.AttrWrite> {
    this.#host.requireRow(sourceId, "set-renderer-display-attr");
    const { rendererId, created } = this.#materializeRendererLocal(sourceId);
    const targetId = rendererId ?? sourceId;
    const target = this.#host.requireRow(targetId, "set-renderer-display-attr");
    Tracing.log("renderer-cow/set-attr", {
      source: sourceId.rawId,
      target: targetId.rawId,
      attrs: Object.keys(patch),
    });
    const change = RowWrites.mergeAttributes("shallow", patch, []);
    if (!created) return await this.#host.writeRowFields(targetId, change, attrContext(targetId, Object.keys(patch)));
    // A renderer materialized by this write: its attributes ride its create
    // (the renderer snapshot carries them), so a single `addRow` persists
    // both — no `addRow` → `updateRow` ordering race. Painted first.
    const attrs = this.#host.rowWrites().begin(targetId, change, RowMetadataStore.ACTOR, this.#host.writeOpts());
    if (this.#host.isEphemeral()) {
      this.#host.rowWrites().accept(attrs);
      return { persisted: "deferred-to-promote" };
    }
    // The renderer snapshot carries `parentId = sourceId`, so the backend
    // links it as a CHILD of the source (server mints the id + returns it).
    let minted: Option<RowId>;
    try {
      minted = await this.#host.backend.addRow(
        this.#host.snapshot(target),
        this.#host.sheetId(),
        null,
        null,
        null,
      );
    } catch (err) {
      this.#host.rowWrites().refuse(attrs, err, "the renderer row was not created");
      this.#undoRendererMaterialize(sourceId, targetId);
      throw RowWriteError.after(
        err,
        "add",
        "POST",
        "/api/rows",
        `the renderer row of ${sourceId.rawId} was not created (nothing reached the server)`,
      );
    }
    this.#host.rowWrites().accept(attrs);
    // Adopt the server-minted id locally so future PATCHes + the joined tree
    // address the persisted renderer (the client-minted id was provisional).
    let rendererLocalId = targetId;
    if (minted != null && minted.toString() !== targetId.rawId) {
      rendererLocalId = this.#rekeyRowLocal(targetId, minted) ?? targetId;
    }
    if (this.#host.row(sourceId) == null) return { persisted: "backend" };
    const flag = this.#host.beginWireAttributes(sourceId);
    try {
      await this.#host.rowWrites().run(flag, this.#host.rowTransport(sourceId), "");
    } catch (err) {
      // The create reached the server: without the source's render-less flag
      // the next load would show two renderers, so delete it again.
      const created = minted ?? RowId.fromLocalId(targetId);
      const deleted = await this.#host.backend.deleteRow(created, null).then(() => true, () => false);
      this.#undoRendererMaterialize(sourceId, rendererLocalId);
      throw flagRefused(
        err,
        `the render-less flag of ${sourceId.rawId} was not saved; the renderer row ` +
          `${created.toString()} had reached the server and ` +
          (deleted ? "was deleted again" : "could not be deleted (it remains on the server)"),
      );
    }
    return { persisted: "backend" };
  }

  /**
   * Undo `#materializeRendererLocal` after its persist failed: drop the real
   * renderer `rendererId`, clear the source's render-less flag, re-synthesize
   * the virtual default renderer (as `#resetRendererToDefault` does locally).
   * The source's attribute bag is the row-field ledger's: a refused flag
   * write was taken back there.
   */
  #undoRendererMaterialize(
    sourceId: RowLocalId,
    rendererId: RowLocalId,
  ): void {
    if (this.#host.has(rendererId)) this.#host.dropRowSubtree(rendererId);
    this.#host.stores().renderless.clear(sourceId);
    const source = this.#host.row(sourceId);
    if (source instanceof SourceRow) {
      const child = this.#host.synthesizeRenderer(
        source,
        shapeFromMeta(this.#host.stores().rowMetadata.get(sourceId).meta ?? null),
      );
      if (child != null && !this.#host.has(child.id)) {
        this.#host.adoptChild(child, sourceId);
      }
    }
    Tracing.log("renderer-cow/undo", { source: sourceId.rawId, renderer: rendererId.rawId });
  }

  /**
   * Re-key a freshly-materialized local row from its provisional client id to
   * the server-authoritative id (mirrors the promote/detach re-key). Snapshot →
   * `remapRowIds` → drop → re-add as the real id, preserving its parent edge +
   * attributes. The joined client tree picks the new id up on the next
   * structural re-render. Renderer rows carry no cells, so none are lost.
   */
  #rekeyRowLocal(oldId: RowLocalId, newId: RowId): Option<RowLocalId> {
    const row = this.#host.row(oldId);
    if (row == null) return null;
    const remapped = remapRowIds(
      this.#host.snapshot(row),
      new Map([[oldId.rawId, newId.toString()]]),
    );
    this.#host.dropRowSubtree(oldId);
    const { cells: _cells, ...spec } = remapped;
    const real = this.#host.addRow(spec);
    Tracing.log("renderer-cow/rekey", { from: oldId.rawId, to: newId.toString() });
    return real.id;
  }

  /**
   * Reset a render-less source back to its synthesized default renderer (the
   * `resetAlias` analogue for default-renderer copy-on-write). Deletes the
   * materialized real renderer row, clears the render-less flag, re-synthesizes
   * the virtual renderer locally, and persists the deletion + the source's
   * cleared flag. No-op if the source isn't render-less. Destructive: renderer
   * customizations on the materialized row are discarded.
   */
  async resetRendererToDefault(sourceId: RowLocalId): Promise<void> {
    const source = this.#host.row(sourceId);
    if (!(source instanceof SourceRow)) return;
    if (!this.#host.stores().renderless.isRenderless(sourceId)) return;

    // The materialized renderer is a real, server-backed row. Capture its
    // RowId (and its snapshot, to put it back on a refusal) before dropping
    // it locally.
    let realRendererId: Option<RowId> = null;
    let realRenderer: Option<RawRow> = null;
    for (const childId of this.#host.stores().tree.children(sourceId)) {
      const child = this.#host.row(childId);
      if (
        child != null &&
        child.descriptor.role === "renderer" &&
        childId.key.isUser()
      ) {
        realRendererId = RowId.fromLocalId(childId);
        realRenderer = this.#host.snapshot(child);
        this.#host.dropRowSubtree(childId);
        break;
      }
    }

    // Clear render-less, then re-synthesize the virtual default renderer
    // (mirrors `addRow`'s synthesis block).
    this.#host.stores().renderless.clear(sourceId);
    const synthesized = this.#synthesizeDefaultRenderer(source);

    Tracing.log("renderer-cow/reset", { source: sourceId.rawId });

    // The source's cleared flag (`snapshotRow` no longer stamps `renderless`),
    // painted into every copy of the row at once.
    const flag = this.#host.beginWireAttributes(sourceId);
    // Persist: delete the materialized renderer, then the source's cleared flag.
    if (realRendererId !== null) {
      try {
        await this.#host.backend.deleteRow(realRendererId, null);
      } catch (err) {
        // Nothing reached the server: the reset is taken back whole — the
        // flag, the synthesized renderer, the materialized one.
        this.#host.rowWrites().refuse(flag, err, `the reset of ${sourceId.rawId}'s renderer was not saved`);
        if (synthesized != null && this.#host.has(synthesized)) this.#host.dropRowSubtree(synthesized);
        this.#host.stores().renderless.mark(sourceId);
        if (realRenderer != null) {
          const { cells: _cells, ...spec } = realRenderer;
          this.#host.addRow(spec);
        }
        throw RowWriteError.after(
          err,
          "delete",
          "DELETE",
          "/api/rows/{rowKey}",
          `the renderer row of ${sourceId.rawId} was not deleted (nothing reached the server); its reset was taken back`,
        );
      }
    }
    try {
      await this.#host.rowWrites().run(flag, this.#host.rowTransport(sourceId), `the cleared render-less flag of ${sourceId.rawId} was not saved`);
    } catch (err) {
      // The renderer row is gone on the server but the source still carries
      // its render-less flag there: the ledger took the flag back, and the
      // sheet shows what the server holds — no renderer at all.
      if (synthesized != null && this.#host.has(synthesized)) this.#host.dropRowSubtree(synthesized);
      this.#host.stores().renderless.mark(sourceId);
      throw err;
    }
  }

  /**
   * Re-synthesize `source`'s virtual default renderer (mirrors `addRow`'s
   * synthesis block); answers its id, `null` when it has none (or already
   * holds one).
   */
  #synthesizeDefaultRenderer(source: SourceRow): Option<RowLocalId> {
    const child = this.#host.synthesizeRenderer(
      source,
      shapeFromMeta(this.#host.stores().rowMetadata.get(source.id).meta ?? null),
    );
    if (child == null || this.#host.has(child.id)) return null;
    this.#host.adoptChild(child, source.id);
    return child.id;
  }

  /**
   * Peek the current value of a renderer display option (a synchronous read
   * of the loaded row metadata), dual-reading the
   * renderer child's OWN bag first (where a customization lives once
   * materialized) and falling back to the source's bag (where un-customized
   * options live, including while the default renderer is still virtual). The
   * read counterpart of `setRendererDisplayAttr`; a host's per-plugin option
   * editors initialize their control from this so a reopened editor shows the
   * customized value, not the source's stale default. Returns `null` when the
   * value is absent or non-scalar.
   */
  peekRendererDisplayAttr(
    sourceId: RowLocalId,
    attr: string,
  ): Option<string | number | boolean> {
    for (const childId of this.#host.stores().tree.children(sourceId)) {
      const child = this.#host.row(childId);
      if (child != null && child.descriptor.role === "renderer") {
        const own = this.#host.stores().rowMetadata.get(childId).attributes?.[attr];
        if (
          typeof own === "string" || typeof own === "number" ||
          typeof own === "boolean"
        ) {
          return own;
        }
        break; // a source has a single renderer child; its bag wins if set
      }
    }
    const src = this.#host.stores().rowMetadata.get(sourceId).attributes?.[attr];
    return typeof src === "string" || typeof src === "number" ||
        typeof src === "boolean"
      ? src
      : null;
  }
}
